import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@component/dialog";
import { Button } from "@component/button";
import { RadioGroup, RadioGroupItem } from "@component/radio-group";
import { Label } from "@component/label";
import { AlertTriangleIcon } from "lucide-react";
import { useEffect, useState } from "react";

interface Props {
    isOpen: boolean;
    countApplicants: number;
    onClose: () => void;
    onConfirmWithEmail: () => void;
    onConfirmWithoutEmail: () => void;
}

export default function CloseJobConfirmationDialog({
    isOpen,
    countApplicants = 0,
    onClose,
    onConfirmWithEmail,
    onConfirmWithoutEmail,
}: Props) {
    const [selectedOption, setSelectedOption] = useState<
        "close-only" | "close-and-notify"
    >("close-only");

    useEffect(() => {
        return () => {
            setSelectedOption("close-only");
        }
    }, [])

    const handleConfirm = () => {
        if (selectedOption === "close-and-notify") {
            onConfirmWithEmail();
        } else {
            onConfirmWithoutEmail();
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-[500px] gap-4">
                <DialogHeader className="space-y-3">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/20 flex items-center justify-center flex-shrink-0">
                            <AlertTriangleIcon className="h-5 w-5 text-red-600 dark:text-red-500" />
                        </div>
                        <DialogTitle className="text-lg font-semibold">
                            Close Job Listing
                        </DialogTitle>
                    </div>
                </DialogHeader>

                <p className="text-sm text-muted-foreground -mt-2">
                    Closing this job will stop all new applications permanently. Choose
                    how you would like to close this job listing.
                </p>

                <div className="space-y-4">
                    <RadioGroup
                        value={selectedOption}
                        onValueChange={(value: any) => setSelectedOption(value)}
                        className="space-y-3"
                    >
                        {/* Option 1: Close job only */}
                        <div className="flex items-start space-x-3 p-4 border border-border rounded-lg hover:bg-accent/50 cursor-pointer">
                            <RadioGroupItem
                                value="close-only"
                                id="close-only"
                                className="mt-0.5"
                            />

                            <Label htmlFor="close-only" className="flex-1 cursor-pointer">
                                <div className="font-semibold text-sm mb-1">
                                    Close Job Listing
                                </div>
                                <div className="text-sm text-muted-foreground">
                                    Closes the job listing and notifies your internal team.
                                    <br />
                                    No emails will be sent to candidates.
                                </div>
                            </Label>
                        </div>

                        {/* Option 2: Close job and notify candidates */}
                        <div className="flex items-start space-x-3 p-4 border border-border rounded-lg hover:bg-accent/50 cursor-pointer">
                            <RadioGroupItem
                                value="close-and-notify"
                                disabled={countApplicants === 0}
                                id="close-and-notify"
                                className="mt-0.5"
                            />

                            <Label
                                htmlFor="close-and-notify"
                                className="flex-1 cursor-pointer"
                            >
                                <div className="font-semibold text-sm mb-1">
                                    Close Job Listing and Notify Applicants
                                </div>
                                <div className="text-sm text-muted-foreground">
                                    Closes the job listing and sends a closure email to all active
                                    applicants.
                                </div>
                            </Label>
                        </div>
                    </RadioGroup>

                    {/* Warning message */}
                    <p className="text-xs text-muted-foreground italic">
                        This action cannot be undone.
                    </p>
                </div>

                {/* Footer CTAs */}
                <div className="flex gap-2 mt-6">
                    <Button onClick={onClose} variant="outline" className="flex-1">
                        Cancel
                    </Button>
                    <Button
                        onClick={handleConfirm}
                        variant="destructive"
                        className="flex-1"
                    >
                        Confirm & Close
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
